import { getFirestore, doc, updateDoc, Timestamp, collection, query, where, onSnapshot } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import PrimaryButton from '../components/buttons/PrimaryButton';
import Input from '../components/inputs/Input';
import SelectInput from '../components/inputs/SelectInput';
import { app } from '../firebase';
import { isRentalManager, useUser } from '../contexts/UserContext';
import Label from '../components/texts/Label';
import { datetimeFromTimestampToInput, datetimeFromTimestampToString } from '../librairies/Dates';
import DateTimeInput from '../components/inputs/DateTimeInput';
import TextareaInput from '../components/inputs/TextareaInput';
import CheckboxInput from '../components/inputs/CheckboxInput';
import {
  buildDaySlots,
  buildDayWindowBlocks,
  buildSlotTextsWithAcceptedAnswers,
  computeSlotMeta,
  countAcceptedAnswersForSlotText,
  durationValueToMinutes,
  extractDatePart,
  extractTimePart,
  formatDayLabelFromDayKey,
  formatDateTimeLocal,
  formatMultiDaySlotTitle,
  formatSlotDayLines,
  getLocalDayKey,
  getDayKeysInRange,
  getDayWindowBlockId,
  getResponseStartTs,
  getSlotCapacity,
  getSlotDays,
  sortCreneauResponsesByStartTs,
  inferDayStartTimesFromResponses,
  inferDayWindowsFromResponses,
  isCreneauResponseDeletable,
  toStoredCreneauSlot,
} from './appointmentSlots';

const db = getFirestore(app);

function UpdateSurvey({ onClose, data }) {
  const user = useUser();
  const [textResponses, setTextResponses] = useState([{ text: "" }]);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState(() => data.data().title || "");
  const [expirationDate, setExpirationDate] = useState(datetimeFromTimestampToInput(data.data().dateEnd.seconds));
  const [complementaryInfo, setComplementaryInfo] = useState(
    () => (data.data().complementaryInfo != null ? String(data.data().complementaryInfo) : "")
  );

  const isAppointmentSurvey = data.data().type === "creneau";
  const slotMeta = useMemo(() => computeSlotMeta(data.data()), [data]);

  const [extensionFirst, setExtensionFirst] = useState("");
  const [extensionLast, setExtensionLast] = useState("");
  const [durationValue, setDurationValue] = useState(slotMeta.durationValue);
  const [durationUnit, setDurationUnit] = useState(slotMeta.durationUnit);
  const [dayStartTimes, setDayStartTimes] = useState({});
  const [dayWindows, setDayWindows] = useState({});
  const [responsesDraft, setResponsesDraft] = useState([]);
  const [selectedNewSlots, setSelectedNewSlots] = useState([]);
  const [selectedSlotDay, setSelectedSlotDay] = useState("");
  const [acceptedSurveyAnswers, setAcceptedSurveyAnswers] = useState([]);
  const [receiveEmail, setReceiveEmail] = useState(
    () => data.data().receiveEmail !== false
  );
  const rentalManager = isRentalManager(user.user);
  const [initialTargets] = useState(() => ({
    coownershipComiteeTarget: data.data().coownershipComiteeTarget === true,
    coownerTarget: data.data().coownerTarget === true,
    tenantTarget: data.data().tenantTarget === true,
  }));
  const [hasTargeting] = useState(() =>
    data.data().coownershipComiteeTarget !== undefined ||
    data.data().coownerTarget !== undefined ||
    data.data().tenantTarget !== undefined
  );
  const [coownershipComiteeTarget, setCoownershipComiteeTarget] = useState(initialTargets.coownershipComiteeTarget);
  const [coownerTarget, setCoownerTarget] = useState(initialTargets.coownerTarget);
  const [tenantTarget, setTenantTarget] = useState(initialTargets.tenantTarget);

  useEffect(() => {
    setTitle(data.data().title || "");
    setExpirationDate(datetimeFromTimestampToInput(data.data().dateEnd.seconds));
    setComplementaryInfo(
      data.data().complementaryInfo != null ? String(data.data().complementaryInfo) : ""
    );
    setTextResponses([{ text: "" }]);
    setSelectedNewSlots([]);
    setSelectedSlotDay("");

    const meta = computeSlotMeta(data.data());
    const existingResponses = data.data().responses || [];
    setResponsesDraft(existingResponses);
    setDurationValue(meta.durationValue);
    setDurationUnit(meta.durationUnit);
    setExtensionFirst(extractDatePart(formatDateTimeLocal(new Date(meta.boundFirstMs))));
    setExtensionLast(extractDatePart(formatDateTimeLocal(new Date(meta.boundLastMs))));
    setDayStartTimes(inferDayStartTimesFromResponses(existingResponses));
    setDayWindows(inferDayWindowsFromResponses(existingResponses));
    setReceiveEmail(data.data().receiveEmail !== false);
  }, [data.id]);

  useEffect(() => {
    if (!isAppointmentSurvey) {
      setAcceptedSurveyAnswers([]);
      return undefined;
    }

    const answersRef = collection(
      db,
      `${user.user.userData.lastCoownershipLoaded.path}/surveys/${data.id}/answers`
    );
    const answersQuery = query(answersRef, where("status", "==", "accepted"));
    const unsubscribe = onSnapshot(answersQuery, (snapshot) => {
      setAcceptedSurveyAnswers(snapshot.docs.map((answerDoc) => answerDoc.data()));
    });

    return () => unsubscribe();
  }, [data.id, isAppointmentSurvey, user.user.userData.lastCoownershipLoaded.path]);

  const reservedSlotTexts = useMemo(
    () => buildSlotTextsWithAcceptedAnswers(acceptedSurveyAnswers),
    [acceptedSurveyAnswers]
  );

  const orderedResponsesDraft = useMemo(
    () => sortCreneauResponsesByStartTs(responsesDraft, acceptedSurveyAnswers),
    [responsesDraft, acceptedSurveyAnswers]
  );

  const dayKeysInRange = useMemo(
    () => getDayKeysInRange(extensionFirst, extensionLast),
    [extensionFirst, extensionLast]
  );

  const normalizedDayStartTimes = useMemo(() => {
    const defaultStartTime = extractTimePart(extensionFirst);
    const inferred = inferDayStartTimesFromResponses(responsesDraft);
    const next = {};
    dayKeysInRange.forEach((dayKey) => {
      next[dayKey] = dayStartTimes[dayKey] || inferred[dayKey] || defaultStartTime;
    });
    return next;
  }, [dayKeysInRange, dayStartTimes, extensionFirst, responsesDraft]);

  const handleDayStartTimeChange = (dayKey, value) => {
    setDayStartTimes((prev) => ({
      ...prev,
      [dayKey]: value,
    }));
  };

  const normalizedDayWindows = useMemo(() => {
    const inferred = inferDayWindowsFromResponses(responsesDraft);
    const next = {};
    dayKeysInRange.forEach((dayKey) => {
      next[dayKey] = {
        start: dayWindows[dayKey]?.start || inferred[dayKey]?.start || "09:00",
        end: dayWindows[dayKey]?.end || inferred[dayKey]?.end || "17:00",
      };
    });
    return next;
  }, [dayKeysInRange, dayWindows, responsesDraft]);

  const handleDayWindowChange = (dayKey, field, value) => {
    setDayWindows((prev) => ({
      ...prev,
      [dayKey]: {
        start: prev[dayKey]?.start || normalizedDayWindows[dayKey]?.start || "09:00",
        end: prev[dayKey]?.end || normalizedDayWindows[dayKey]?.end || "17:00",
        [field]: value,
      },
    }));
  };

  const durationMinutes = durationValueToMinutes(durationValue, durationUnit);

  const generatedSlots = useMemo(() => {
    if (!isAppointmentSurvey) return [];
    if (durationUnit === "days") {
      return buildDayWindowBlocks(dayKeysInRange, normalizedDayWindows, Number(durationValue));
    }
    return dayKeysInRange.flatMap((dayKey) =>
      buildDaySlots(dayKey, normalizedDayStartTimes[dayKey], durationMinutes)
    );
  }, [
    isAppointmentSurvey,
    durationUnit,
    durationValue,
    dayKeysInRange,
    normalizedDayStartTimes,
    normalizedDayWindows,
    durationMinutes,
  ]);

  const selectedNewSlotIds = useMemo(
    () => new Set(selectedNewSlots.map((slot) => String(slot.id))),
    [selectedNewSlots]
  );

  const selectableGridSlots = generatedSlots;

  const slotsByDay = useMemo(() => {
    const acc = {};
    dayKeysInRange.forEach((dayKey) => {
      acc[dayKey] = {
        label: formatDayLabelFromDayKey(dayKey),
        slots: [],
      };
    });
    selectableGridSlots.forEach((slot) => {
      if (acc[slot.dayKey]) {
        acc[slot.dayKey].slots.push(slot);
      }
    });
    return acc;
  }, [dayKeysInRange, selectableGridSlots]);

  const dayKeys = dayKeysInRange;
  const currentDayKey =
    selectedSlotDay && slotsByDay[selectedSlotDay] ? selectedSlotDay : dayKeys[0] || "";

  const daysWithExistingSlots = useMemo(() => {
    return new Set(
      responsesDraft
        .map((response) => {
          const startTs = getResponseStartTs(response);
          return startTs != null ? getLocalDayKey(new Date(startTs)) : null;
        })
        .filter((dayKey) => dayKey != null)
    );
  }, [responsesDraft]);

  const selectedDayKeys = useMemo(() => {
    return new Set(
      selectedNewSlots.map((slot) => getLocalDayKey(new Date(slot.startTs)))
    );
  }, [selectedNewSlots]);

  const orderedSelectedNewSlots = useMemo(
    () => [...selectedNewSlots].sort((a, b) => a.startTs - b.startTs),
    [selectedNewSlots]
  );

  const matchesExistingSlot = (slot, item) => {
    if (getSlotDays(slot).length > 0) {
      return getDayWindowBlockId(item) === slot.id;
    }
    return getResponseStartTs(item) === slot.startTs && getSlotDays(item).length === 0;
  };

  const addNewSlot = (slot) => {
    if (responsesDraft.some((item) => matchesExistingSlot(slot, item))) {
      setError("");
      setResponsesDraft((prev) =>
        prev.map((item) =>
          matchesExistingSlot(slot, item)
            ? { ...item, capacity: getSlotCapacity(item) + 1 }
            : item
        )
      );
      return;
    }
    if (selectedNewSlotIds.has(String(slot.id))) {
      setError("");
      setSelectedNewSlots((prev) =>
        prev.map((item) =>
          String(item.id) === String(slot.id)
            ? { ...item, capacity: getSlotCapacity(item) + 1 }
            : item
        )
      );
      return;
    }
    setError("");
    setSelectedNewSlots((prev) => [
      ...prev,
      {
        id: slot.id,
        text: slot.text,
        startTs: slot.startTs,
        endTs: slot.endTs,
        capacity: 1,
        ...(slot.days ? { days: slot.days } : {}),
      },
    ]);
  };

  const decreaseNewSlot = (slotId) => {
    setSelectedNewSlots((prev) =>
      prev.flatMap((slot) => {
        if (String(slot.id) !== String(slotId)) return [slot];
        const nextCapacity = getSlotCapacity(slot) - 1;
        if (nextCapacity < 1) return [];
        return [{ ...slot, capacity: nextCapacity }];
      })
    );
  };

  const removeNewSlot = (slotId) => {
    setSelectedNewSlots((prev) => prev.filter((slot) => String(slot.id) !== String(slotId)));
  };

  const changeExistingCapacity = (startTs, delta) => {
    setResponsesDraft((prev) =>
      prev.map((item) => {
        if (getResponseStartTs(item) !== startTs) return item;
        const acceptedCount = countAcceptedAnswersForSlotText(item.text, acceptedSurveyAnswers);
        const nextCapacity = getSlotCapacity(item) + delta;
        if (nextCapacity < Math.max(acceptedCount, 1)) return item;
        return { ...item, capacity: nextCapacity };
      })
    );
  };

  const capacityForSlot = (slot) => {
    const existing = responsesDraft.find((item) => matchesExistingSlot(slot, item));
    if (existing) return getSlotCapacity(existing);
    const created = selectedNewSlots.find((item) => String(item.id) === String(slot.id));
    return created ? getSlotCapacity(created) : 0;
  };

  const removeExistingSlot = (startTs) => {
    const response = responsesDraft.find((item) => getResponseStartTs(item) === startTs);
    if (!response || !isCreneauResponseDeletable(response, reservedSlotTexts)) return;
    setResponsesDraft((prev) => prev.filter((item) => getResponseStartTs(item) !== startTs));
  };

  const handleAddTextResponse = () => {
    setTextResponses([...textResponses, { text: "" }]);
  };

  const handleTextResponseChange = (index, value) => {
    const next = [...textResponses];
    next[index] = { text: value };
    setTextResponses(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("La question du sondage est requise.");
      return;
    }

    if (isAppointmentSurvey) {
      if (!extensionFirst || !extensionLast) {
        setError("Les dates de première et dernière intervention sont requises.");
        return;
      }
      if (!Number.isInteger(Number(durationValue)) || durationMinutes <= 0) {
        setError("La durée d'intervention doit être un nombre entier supérieur à 0.");
        return;
      }
      if (extensionFirst > extensionLast) {
        setError("La première intervention doit être avant ou égale à la dernière.");
        return;
      }
      if (responsesDraft.length === 0 && selectedNewSlots.length === 0) {
        setError("Le sondage doit conserver au moins un créneau.");
        return;
      }
    }

    const nextTargets = {
      coownershipComiteeTarget: rentalManager
        ? initialTargets.coownershipComiteeTarget
        : initialTargets.coownershipComiteeTarget || coownershipComiteeTarget,
      coownerTarget: rentalManager
        ? initialTargets.coownerTarget
        : initialTargets.coownerTarget || coownerTarget,
      tenantTarget: initialTargets.tenantTarget || tenantTarget,
    };

    if (hasTargeting && !nextTargets.coownerTarget && !nextTargets.coownershipComiteeTarget && !nextTargets.tenantTarget) {
      setError("Vous devez sélectionner au minimum une cible pour le sondage");
      return;
    }

    setLoader(true);

    try {
      let responseArray = [...(data.data().responses || [])];

      if (isAppointmentSurvey) {
        const newSlotPayload = [...selectedNewSlots].sort((a, b) => a.startTs - b.startTs);
        responseArray = [...responsesDraft, ...newSlotPayload].map((slot) => toStoredCreneauSlot(slot));
      } else {
        textResponses.forEach((element) => {
          if (element.text) responseArray.push(element);
        });
      }

      const surveyRef = doc(db, `${user.user.userData.lastCoownershipLoaded.path}/surveys/${data.id}`);
      const payload = {
        title: title.trim(),
        responses: responseArray,
        dateEnd: Timestamp.fromDate(new Date(expirationDate)),
        complementaryInfo: complementaryInfo.trim() || null,
      };

      if (isAppointmentSurvey) {
        payload.receiveEmail = receiveEmail;
      }

      if (
        hasTargeting ||
        nextTargets.coownershipComiteeTarget ||
        nextTargets.coownerTarget ||
        nextTargets.tenantTarget
      ) {
        payload.coownershipComiteeTarget = nextTargets.coownershipComiteeTarget;
        payload.coownerTarget = nextTargets.coownerTarget;
        payload.tenantTarget = nextTargets.tenantTarget;
      }

      await updateDoc(surveyRef, payload);

      onClose();
    } catch (err) {
      console.log(err);
      setError("Erreur lors de l'enregistrement du sondage.");
    }

    setLoader(false);
  };

  return (
    <div
      id="authentication-modal"
      tabIndex="-1"
      aria-hidden="true"
      className="mx-auto w-full fixed inset-0 flex items-center justify-center overflow-auto"
    >
      <div className="relative w-full max-w-2xl max-h-full">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
            data-modal-hide="authentication-modal"
          >
            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
          <div className="px-6 py-6 lg:px-8">
            <h3 className="mb-4 text-xl font-medium text-gray-900 dark:text-white">Modifier un sondage</h3>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <div className="border-b border-gray mt-0.5 mb-2" />

                <Input
                  id={"title"}
                  label={"Question"}
                  type={"text"}
                  name={"title"}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />

                <div className="mt-4">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="w-full rounded-xl border border-secondary bg-secondary/10 p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">{isAppointmentSurvey ? "📅" : "📋"}</div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {isAppointmentSurvey ? "Rendez-vous" : "Sondage"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {isAppointmentSurvey
                              ? "Planifier une intervention ou une visite dans les logements privatifs (créneaux proposés automatiquement)"
                              : "Recueillez des avis et validez vos décisions en quelques clics (travaux, devis, préférences…)."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isAppointmentSurvey ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label text={"Créneaux existants"} required={false} />
                      <p className="mb-2 text-xs text-gray-500">
                        Seuls les créneaux sans réponse au statut accepté peuvent être supprimés.
                      </p>
                      <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
                        {orderedResponsesDraft.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun créneau enregistré.</p>
                        ) : (
                          orderedResponsesDraft.map((response, index) => {
                            const deletable = isCreneauResponseDeletable(response, reservedSlotTexts);
                            const startTs = getResponseStartTs(response);
                            const acceptedAnswersCount = countAcceptedAnswersForSlotText(
                              response.text,
                              acceptedSurveyAnswers
                            );
                            const capacity = getSlotCapacity(response);
                            const canDecrease = capacity > Math.max(acceptedAnswersCount, 1);
                            return (
                              <div
                                key={`${startTs}-${index}`}
                                className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                              >
                                <span>
                                  {getSlotDays(response).length > 1 ? formatMultiDaySlotTitle(getSlotDays(response)) : response.text}
                                  {` · ${acceptedAnswersCount}/${capacity} place${capacity > 1 ? "s" : ""}`}
                                  {getSlotDays(response).length > 1 && (
                                    <span className="mt-1 block text-xs font-normal text-gray-600">
                                      {formatSlotDayLines(getSlotDays(response)).join(" · ")}
                                    </span>
                                  )}
                                </span>
                                <span className="flex items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => changeExistingCapacity(startTs, -1)}
                                    disabled={!canDecrease}
                                    className="font-medium text-gray-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-300"
                                  >
                                    −1
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => changeExistingCapacity(startTs, 1)}
                                    className="font-medium text-gray-700 hover:underline"
                                  >
                                    +1
                                  </button>
                                  {deletable ? (
                                    <button
                                      type="button"
                                      onClick={() => removeExistingSlot(startTs)}
                                      className="font-medium text-red-600 hover:underline"
                                    >
                                      Supprimer
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-500">Non supprimable</span>
                                  )}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-4">
                      <Label text={"Ajouter des créneaux"} required={false} />
                      <p className="mb-3 text-xs text-gray-500">
                        La plage de dates sert à proposer de nouveaux créneaux. La modifier ne retire pas ceux déjà choisis.
                        Recliquez sur un créneau pour augmenter sa capacité. Les créneaux peuvent se chevaucher.
                        Un créneau de plusieurs jours se réserve en entier.
                      </p>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Input
                          id="updateInterventionDuration"
                          label="Durée"
                          type="number"
                          name="updateInterventionDuration"
                          value={durationValue}
                          onChange={() => {}}
                          disabled
                          required={false}
                        />
                        <SelectInput
                          id="updateDurationUnit"
                          name="updateDurationUnit"
                          label="Unité"
                          value={durationUnit}
                          onChange={() => {}}
                          disabled
                          required={false}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Heures</option>
                          <option value="days">Jours</option>
                        </SelectInput>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <DateTimeInput
                          id="updateExtensionFirst"
                          label="Première intervention"
                          value={extensionFirst}
                          onChange={setExtensionFirst}
                          dateOnly
                        />
                        <DateTimeInput
                          id="updateExtensionLast"
                          label="Dernière intervention"
                          value={extensionLast}
                          onChange={setExtensionLast}
                          dateOnly
                        />
                      </div>

                      {durationUnit === "days" && dayKeysInRange.length > 0 && (
                        <div className="mt-3 rounded-lg border border-gray-200 p-3">
                          <p className="mb-2 text-sm font-medium text-gray-700">Horaires de chaque jour</p>
                          <div className="grid grid-cols-1 gap-3">
                            {dayKeysInRange.map((dayKey) => (
                              <div key={dayKey} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-2 text-sm font-medium text-gray-700">
                                  {new Intl.DateTimeFormat("fr-FR", {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  }).format(new Date(`${dayKey}T00:00:00`))}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="text-xs text-gray-600">
                                    Début
                                    <input
                                      type="time"
                                      value={normalizedDayWindows[dayKey].start}
                                      step={300}
                                      onChange={(e) => handleDayWindowChange(dayKey, "start", e.target.value)}
                                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </label>
                                  <label className="text-xs text-gray-600">
                                    Fin
                                    <input
                                      type="time"
                                      value={normalizedDayWindows[dayKey].end}
                                      step={300}
                                      onChange={(e) => handleDayWindowChange(dayKey, "end", e.target.value)}
                                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {durationUnit !== "days" && dayKeysInRange.length > 0 && (
                        <div className="mt-3 rounded-lg border border-gray-200 p-3">
                          <p className="mb-2 text-sm font-medium text-gray-700">Heure de début par jour</p>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {dayKeysInRange.map((dayKey) => (
                              <div key={dayKey} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-1 text-sm font-medium text-gray-700">
                                  {new Intl.DateTimeFormat("fr-FR", {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  }).format(new Date(`${dayKey}T00:00:00`))}
                                </p>
                                <input
                                  type="time"
                                  value={normalizedDayStartTimes[dayKey]}
                                  step={300}
                                  onChange={(e) => handleDayStartTimeChange(dayKey, e.target.value)}
                                  className="w-full rounded-lg border border-gray-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {dayKeysInRange.length > 0 ? (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3">
                          {durationUnit !== "days" && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {dayKeys.map((dayKey) => (
                              <button
                                key={dayKey}
                                type="button"
                                onClick={() => setSelectedSlotDay(dayKey)}
                                className={`rounded-md px-3 py-1 text-sm ${
                                  currentDayKey === dayKey
                                    ? "bg-secondary text-white"
                                    : selectedDayKeys.has(dayKey)
                                      ? "bg-primary text-white"
                                      : daysWithExistingSlots.has(dayKey)
                                        ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                {slotsByDay[dayKey].label}
                              </button>
                            ))}
                          </div>
                          )}

                          {durationUnit === "days" ? (
                            generatedSlots.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {generatedSlots.map((slot) => {
                                  const capacity = capacityForSlot(slot);
                                  return (
                                    <button
                                      key={slot.id}
                                      type="button"
                                      onClick={() => addNewSlot(slot)}
                                      className={`rounded-lg px-3 py-2 text-left text-sm ${
                                        capacity > 0
                                          ? "border border-secondary bg-secondary/20 font-bold text-black"
                                          : "border border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                    >
                                      <span>{(slot.days?.length > 1 ? formatMultiDaySlotTitle(slot.days) : slot.text)}{capacity > 0 ? ` · ×${capacity}` : ""}</span>
                                      {slot.days?.length > 1 && (
                                        <span className="mt-1 block text-xs font-normal text-gray-600">
                                          {formatSlotDayLines(slot.days).join(" · ")}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">
                                Chaque jour du créneau doit avoir une heure de fin après l'heure de début, et la durée doit tenir dans la plage.
                              </p>
                            )
                          ) : (slotsByDay[currentDayKey]?.slots || []).length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {(slotsByDay[currentDayKey]?.slots || []).map((slot) => {
                                const capacity = capacityForSlot(slot);
                                return (
                                <button
                                  key={slot.id}
                                  type="button"
                                  onClick={() => addNewSlot(slot)}
                                  className={`rounded-lg px-3 py-1.5 text-sm ${
                                    capacity > 0
                                      ? "border border-secondary bg-secondary/20 font-bold text-black"
                                      : "border border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                                >
                                  {slot.timeLabel}{capacity > 0 ? ` · ×${capacity}` : ""}
                                </button>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              {durationUnit === "days"
                                ? "Aucun créneau disponible pour ce jour."
                                : "Aucun nouveau créneau disponible pour ce jour. Modifiez l'heure de début pour en proposer d'autres."}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-gray-500">
                          Ajustez la plage de dates pour proposer de nouveaux créneaux.
                        </p>
                      )}

                      <p className="mt-3 text-xs text-gray-500">
                        <span className="font-bold text-gray-700">
                          {selectedNewSlots.length} nouveau{selectedNewSlots.length > 1 ? "x" : ""} créneau
                          {selectedNewSlots.length > 1 ? "x" : ""} sélectionné{selectedNewSlots.length > 1 ? "s" : ""}
                        </span>
                      </p>

                      <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
                        {orderedSelectedNewSlots.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun nouveau créneau sélectionné.</p>
                        ) : (
                          orderedSelectedNewSlots.map((slot, index) => (
                            <div
                              key={`${slot.startTs}-${index}`}
                              className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                            >
                              <span>
                                {slot.days?.length > 1 ? formatMultiDaySlotTitle(slot.days) : slot.text}
                                {` · ${getSlotCapacity(slot)} place${getSlotCapacity(slot) > 1 ? "s" : ""}`}
                                {slot.days?.length > 1 && (
                                  <span className="mt-1 block text-xs font-normal text-gray-600">
                                    {formatSlotDayLines(slot.days).join(" · ")}
                                  </span>
                                )}
                              </span>
                              <span className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => decreaseNewSlot(slot.id)}
                                  className="font-medium text-gray-700 hover:underline"
                                >
                                  −1
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeNewSlot(slot.id)}
                                  className="font-medium text-red-600 hover:underline"
                                >
                                  Supprimer
                                </button>
                              </span>
                            </div>
                          ))
                        )}
                      </div>

                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <Label text={"Options de réponse existantes"} required={false} />
                    {data.data().responses.map((response, index) => (
                      <div
                        key={index}
                        className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                      >
                        <span className="mr-1 font-medium">-</span>
                        {response.text}
                      </div>
                    ))}
                    <div className="mt-3">
                      <Label text={"Nouvelles options"} required={false} />
                      {textResponses.map((response, index) => (
                        <div key={index} className="mb-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={response.text}
                            onChange={(e) => handleTextResponseChange(index, e.target.value)}
                            className="flex-grow rounded-lg border border-gray-300 p-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                        </div>
                      ))}
                      <button type="button" onClick={handleAddTextResponse} className="text-sm text-blue-500">
                        + Ajouter une réponse
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Label text={"Sondage destiné à"} />
                <div className="flex justify-between">
                  <CheckboxInput
                    id={"coownershipComiteeTarget"}
                    name={"coownershipComiteeTarget"}
                    label={"CS"}
                    checked={coownershipComiteeTarget}
                    onChange={() => setCoownershipComiteeTarget((current) => !current)}
                    disabled={initialTargets.coownershipComiteeTarget || rentalManager}
                  />
                  <CheckboxInput
                    id={"coownerTarget"}
                    name={"coownerTarget"}
                    label={"Copropriétaires"}
                    checked={coownerTarget}
                    onChange={() => setCoownerTarget((current) => !current)}
                    disabled={initialTargets.coownerTarget || rentalManager}
                  />
                  <CheckboxInput
                    id={"tenantTarget"}
                    name={"tenantTarget"}
                    label={"Locataires"}
                    checked={tenantTarget}
                    onChange={() => setTenantTarget((current) => !current)}
                    disabled={initialTargets.tenantTarget}
                  />
                </div>
                <p className="text-xs text-gray-500">Les cibles déjà sélectionnées ne peuvent pas être retirées.</p>
              </div>

              {isAppointmentSurvey && (
                <div className="mt-4">
                  <CheckboxInput
                    id="receiveEmail"
                    name="receiveEmail"
                    label="Recevoir les notifications par e-mail"
                    checked={receiveEmail}
                    onChange={() => setReceiveEmail(!receiveEmail)}
                  />
                </div>
              )}

              <div className="mt-4">
                <DateTimeInput
                  id="expirationDate"
                  label={"Date d'expiration"}
                  value={expirationDate}
                  onChange={setExpirationDate}
                  stepMinutes={5}
                />
              </div>

              <div className="mt-2 text-sm text-gray-600">
                Date de fin enregistrée : {datetimeFromTimestampToString(data.data().dateEnd.seconds)}
              </div>

              <div className="mt-4">
                <TextareaInput
                  id="updateComplementaryInfo"
                  name="complementaryInfo"
                  label="Information complémentaire"
                  value={complementaryInfo}
                  onChange={(e) => setComplementaryInfo(e.target.value)}
                  required={false}
                />
                <p className="text-xs text-gray-500 -mt-1 mb-2">
                  Facultatif. Si ce sondage concerne uniquement une partie des occupants (ex : bâtiment A, rez-de-chaussée…), indiquez-le ici. Cela permet de vérifier que seules les personnes concernées peuvent répondre. Exemple : ce sondage est destiné aux occupants du bâtiment A.
                </p>
              </div>

              <div className="mt-4 text-center">
                <PrimaryButton type="submit" value="Modifier" loading={loader} />
              </div>

              {error !== "" ? <p className="my-1 flex justify-center text-red-500">{error}</p> : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdateSurvey;
